import React, {useEffect, useRef, useState} from 'react';

import CSSModules from 'react-css-modules';

import {CANVAS_RATIO} from '../../Canvas';
import {useFontPaintReady} from '../../hooks/useFontPaintReady';
import type {BaseObject, CanvasSize} from '../../types';
import {BackgroundTypes, TEXT_SIZE_MAP} from '../../types';
import {
  getRectsWithText,
  sanitizeHtmlWithoutTextAlign,
  TEXT_OBJECT_PADDING,
  TEXT_OBJECT_PREVIEW_PADDING,
} from '../../utils/textUtils';
import type {Rect} from '../../utils/textUtils';

import variables from '../../../../resources/styles/colours.module.scss';
import styles from '../../styles.module.scss';

export const INITIAL_TEXT_OBJECT_STYLES = {
  backgroundType: BackgroundTypes.Box,
  backgroundColor: 'white',
  styles: {
    textColor: variables.grey900,
    x: 3,
    y: 8,
    scale: 1,
    rotate: 0,
    fontSize: TEXT_SIZE_MAP.medium,
    textAlign: 'left',
  },
};

interface TextObjectProps<T extends BaseObject> {
  object: Extract<T, {type: 'text'}>;
  canvasSize: CanvasSize;
  handleDoubleClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: T
  ) => void;
  objectPlacementEnabled: boolean;
  isPreviewMode: boolean;
  placeholder?: string;
  onRenderReady?: () => void;
  objectScale?: number;
}

const TextObjectComponent = <T extends BaseObject>({
  object,
  canvasSize,
  handleDoubleClick,
  objectPlacementEnabled,
  isPreviewMode,
  placeholder,
  objectScale,
  onRenderReady,
}: TextObjectProps<T>) => {
  const {width} = canvasSize;
  const scaleFactor = width / CANVAS_RATIO.width;
  const containerRef = useRef<HTMLDivElement>(null);
  const [rects, setRects] = useState<Rect[]>([]);
  const [isRenderReady, setIsRenderReady] = useState(false);

  // iOS devices do not reliably respect the font loaded status so we need to use
  // a more direct approach to ensure the font is painted before we measure the text.
  const fontFamily = 'Commissioner';
  const fontSizePx = `${object.styles.fontSize * scaleFactor}px`;
  const {paintReady} = useFontPaintReady(fontFamily, fontSizePx);

  const isTextPresent = !!sanitizeHtmlWithoutTextAlign(object.htmlContent);

  const textPadding = isPreviewMode
    ? TEXT_OBJECT_PREVIEW_PADDING
    : TEXT_OBJECT_PADDING;

  const getTextColor = () => {
    if (!isTextPresent) {
      return variables.grey300;
    }

    return object.styles.textColor ?? variables.grey900;
  };

  useEffect(() => {
    setIsRenderReady(false);

    if (!paintReady) {
      return;
    }

    const performCreateRects = () => {
      const {
        rects: _rects,
        currentWidth,
        currentHeight,
      } = getRectsWithText(
        sanitizeHtmlWithoutTextAlign(
          object.htmlContent ||
            `<p>${placeholder}</p>` ||
            '<p>Add your text</p>'
        ),
        {
          fontSize: fontSizePx,
          textAlign: object.styles.textAlign,
          fontFamily,
        },
        object.backgroundType,
        scaleFactor,
        objectScale ?? INITIAL_TEXT_OBJECT_STYLES.styles.scale,
        width,
        objectPlacementEnabled,
        isPreviewMode
      );

      if (containerRef.current) {
        containerRef.current.style.width = `${currentWidth}px`;
        containerRef.current.style.height = `${currentHeight}px`;
      }
      setRects(_rects);
    };

    performCreateRects();
    setIsRenderReady(true);
    onRenderReady?.();
  }, [
    paintReady,
    object.styles.fontSize,
    object.styles.textAlign,
    object.backgroundType,
    object.htmlContent,
    width,
    scaleFactor,
    objectPlacementEnabled,
    isPreviewMode,
    placeholder,
    fontSizePx,
    fontFamily,
    onRenderReady,
    objectScale,
  ]);

  return (
    <div
      className='text-container'
      data-automation={`dynamic-text${isPreviewMode ? '-preview' : ''}`}
      ref={containerRef}
      style={{
        wordBreak: 'break-word',
        position: 'relative',
        paddingTop: object.styles.fontSize * scaleFactor * 0.15,
        paddingLeft: textPadding,
        paddingRight: textPadding,
        paddingBottom: object.styles.fontSize * scaleFactor * 0.15,
        boxSizing: 'content-box',
        filter: 'drop-shadow(0px 5px 5px rgba(0,0,0,0.3))',
        visibility: isRenderReady ? 'visible' : 'hidden',
      }}
      onDoubleClick={(e) => {
        handleDoubleClick?.(e, object);
      }}
    >
      {rects.map((rect, index) => (
        <div
          key={index}
          style={{
            ...rect,
            position: 'absolute',
            background: object.backgroundColor,
            zIndex: -1,
          }}
        />
      ))}

      <span
        dir='auto'
        styleName='editable'
        style={{
          fontSize: object.styles.fontSize * scaleFactor,
          color: getTextColor(),
          fontFamily,
        }}
        dangerouslySetInnerHTML={{
          __html:
            sanitizeHtmlWithoutTextAlign(object.htmlContent) ||
            `<p>${placeholder}</p>` ||
            '',
        }}
      />
    </div>
  );
};

export const TextObject = CSSModules(TextObjectComponent, styles, {
  allowMultiple: true,
});
